"use client";

import { useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

// Builds the date string from local year/month/day components -- calling toISOString() on a
// calendar-picked local date can shift it a day in either direction once UTC offset crosses
// midnight, which is exactly the kind of quiet bug a date picker cannot afford.
function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateString(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * A calendar popover replacing the browser's native <input type="date"> -- native date inputs
 * render wildly differently across Chrome/Safari/mobile (see the earlier "28/08/2026" default
 * text rendering inconsistently), where this renders identically everywhere. react-day-picker
 * (MIT, actively maintained, the library shadcn/ui itself wraps) rather than hand-rolling one.
 */
export default function DatePicker({
  value,
  onChange,
  min,
  max,
}: {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseDateString(value) : undefined;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 rounded-lg border border-foreground/20 bg-background px-4 py-2.5 text-sm font-medium transition hover:border-accent"
      >
        <svg viewBox="0 0 24 24" className="size-4 text-muted" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
          <path d="M8 3v4M16 3v4M3.5 10h17" />
        </svg>
        {selected
          ? selected.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })
          : "Pick a date"}
      </button>

      {open && (
        <>
          <button
            aria-label="Close date picker"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          {/* left-1/2 -translate-x-1/2 below `lg`: the trigger button is centered in its own
              container there (see SlotStep.tsx), so anchoring the panel to the button's left
              edge pushed a chunk of it (including the next-month arrow) off the right side of
              narrow screens. Centering it on the button instead keeps it on-screen; `lg:` goes
              back to left-edge-aligned, matching the button's own left-aligned position there. */}
          {/* max-h + overflow-y-auto: a defensive floor under the parent's overflow-visible fix
              (see FormSplitLayout.tsx) -- on a short viewport (a phone in landscape, a small
              laptop window) the calendar's own natural height can still exceed what's actually
              visible below the trigger; this makes it scroll internally instead of ever running
              off-screen, vertically, on top of the horizontal fix above. */}
          <div className="date-picker-panel absolute left-1/2 top-full z-20 mt-2 max-h-[min(420px,70vh)] max-w-[calc(100vw-3rem)] -translate-x-1/2 overflow-y-auto rounded-xl border border-foreground/10 bg-surface p-3 shadow-[0_1px_2px_rgba(20,17,13,0.05),0_16px_40px_rgba(20,17,13,0.12)] lg:left-0 lg:max-w-none lg:translate-x-0">
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={(date) => {
                if (!date) return;
                onChange(toDateString(date));
                setOpen(false);
              }}
              disabled={[
                ...(min ? [{ before: parseDateString(min) }] : []),
                ...(max ? [{ after: parseDateString(max) }] : []),
              ]}
              defaultMonth={selected ?? (min ? parseDateString(min) : undefined)}
            />
          </div>
        </>
      )}
    </div>
  );
}
